import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CHILD_AGE_MONTH_OPTIONS,
  CHILD_AGE_YEAR_OPTIONS,
} from "helpers/dataConversion/convertClassAgeRange";
import { trackHomeClassFinderSubmit } from "helpers/analytics/trackHomeClassFinder";
import {
  DAY_KEYS,
  DAY_LABELS,
  DEFAULT_CLASS_SCHEDULE_FILTER,
} from "helpers/jackrabbit/classScheduleFilter";
import {
  HOME_FINDER_PROGRAMS,
  resolveProgramForChildAge,
} from "helpers/jackrabbit/programAgeBounds";
import { buildProgramScheduleUrl } from "helpers/jackrabbit/scheduleFilterSearchParams";
import "styles/siteChrome.css";
import "./HomeClassFinder.css";

const HOME_AGE_YEAR_OPTIONS = CHILD_AGE_YEAR_OPTIONS.filter(
  (option) => option.value !== ""
);

const HomeClassFinder = () => {
  const navigate = useNavigate();
  const [childAgeYears, setChildAgeYears] = useState("");
  const [childAgeMonths, setChildAgeMonths] = useState("0");
  const [days, setDays] = useState([]);
  const [error, setError] = useState("");

  const toggleDay = (day) => {
    setDays((previous) =>
      previous.includes(day)
        ? previous.filter((value) => value !== day)
        : [...previous, day]
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setError("");

    if (childAgeYears === "") {
      setError("Please select your child's age to see matching classes.");
      return;
    }

    const filter = {
      ...DEFAULT_CLASS_SCHEDULE_FILTER,
      childAgeYears: Number(childAgeYears),
      childAgeMonths: Number(childAgeMonths || 0),
      days,
    };

    let programName = resolveProgramForChildAge(
      filter.childAgeYears,
      filter.childAgeMonths
    );

    if (!HOME_FINDER_PROGRAMS.includes(programName)) {
      programName = "Recreational";
    }

    trackHomeClassFinderSubmit(programName, filter);
    navigate(buildProgramScheduleUrl(programName, filter, { source: "home_finder" }));
  };

  return (
    <section
      id="find-a-class"
      className="home-class-finder"
      aria-labelledby="home-class-finder-heading"
    >
      <div className="home-class-finder__inner">
        <header className="home-class-finder__header">
          <p className="home-kicker">Find a class</p>
          <h2 id="home-class-finder-heading" className="home-class-finder__title">
            Not sure where to start?
          </h2>
          <p className="home-class-finder__lead">
            Tell us your child&apos;s age and optional days — we&apos;ll take you
            to the right program schedule with matching classes ready to browse.
          </p>
        </header>

        <form className="home-class-finder__form" onSubmit={handleSubmit}>
          <fieldset className="home-class-finder__group">
            <legend className="home-class-finder__legend">How old is your child?</legend>
            <div className="home-class-finder__age-inputs">
              <label className="home-class-finder__age-field">
                <span className="home-class-finder__age-label">Years</span>
                <select
                  className="home-class-finder__select"
                  value={childAgeYears}
                  onChange={(event) => setChildAgeYears(event.target.value)}
                  required
                >
                  <option value="">Select age</option>
                  {HOME_AGE_YEAR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label
                className={`home-class-finder__age-field${
                  childAgeYears === ""
                    ? " home-class-finder__age-field--disabled"
                    : ""
                }`}
              >
                <span className="home-class-finder__age-label">Months</span>
                <select
                  className="home-class-finder__select"
                  value={childAgeMonths}
                  onChange={(event) => setChildAgeMonths(event.target.value)}
                  disabled={childAgeYears === ""}
                >
                  {CHILD_AGE_MONTH_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </fieldset>

          <fieldset className="home-class-finder__group">
            <legend className="home-class-finder__legend">
              Which days work best? <span className="home-class-finder__optional">(optional)</span>
            </legend>
            <p className="home-class-finder__hint">
              Select one or more days — we&apos;ll show classes that meet on any of them.
            </p>
            <div className="home-class-finder__chips">
              {DAY_KEYS.map((day) => {
                const selected = days.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    className={`home-class-finder__chip${
                      selected ? " home-class-finder__chip--selected" : ""
                    }`}
                    aria-pressed={selected}
                    onClick={() => toggleDay(day)}
                  >
                    {DAY_LABELS[day]}
                  </button>
                );
              })}
            </div>
          </fieldset>

          {error && (
            <p className="home-class-finder__error" role="alert">
              {error}
            </p>
          )}

          <button type="submit" className="home-btn home-btn--primary home-class-finder__submit">
            See matching classes
          </button>
        </form>
      </div>
    </section>
  );
};

export default HomeClassFinder;
